import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import ContentPasteOutlinedIcon from "@mui/icons-material/ContentPasteOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import * as React from "react";
import { AppointmentTooltip as DXAppointmentTooltip } from "@devexpress/dx-react-scheduler-material-ui";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import LocalActivityIcon from "@mui/icons-material/LocalActivity";
import PublicIcon from "@mui/icons-material/Public";
import classNames from "clsx";
import { format } from "date-fns";
import { formatBackendDateOnly } from "utils/common";
import {
  RECURRENCE_DAYS,
  RECURRENCE_WEEKS,
  REOCCURRENCE_PERIOD_TYPE_MONTHLY,
  REOCCURRENCE_PERIOD_TYPE_WEEKLY,
  REOCCURRENCE_PERIODS,
} from "constants/appointments";
import { useAppSelector } from "app/hooks";
import { selectNumberOfPracticeClinics } from "features/auth/auth-slice";
import { PersonalEvent } from "services/types/events.type";
import PreviewContentRow from "./appointment-preview-content/preview-content-row.component";
import { classes } from "./appointment-preview.component";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import EventIcon from "@mui/icons-material/Event";
import { getRRuleText } from "components/recurrence-rule-editor/utils";

type PersonalEventPreviewProps = {
  appointmentData: DXAppointmentTooltip.ContentProps["appointmentData"] &
    PersonalEvent;
  formatDate: DXAppointmentTooltip.ContentProps["formatDate"];
};
export default function PersonalEventPreview({
  appointmentData,
  formatDate,
}: PersonalEventPreviewProps) {
  const numberOfPracticeClinics =
    useAppSelector(selectNumberOfPracticeClinics) || 0;
  const multiClinic = numberOfPracticeClinics > 1;
  const {
    title = "",
    startDate,
    endDate,
    recurrenceDay,
    recurrencePeriod,
    recurrenceWeek,
    endRecurrenceDate,
    clinic,
    notes,
    isClinicEvent,
    isGlobalPracticeEvent,
    appointment_type,
    staff_members = [],
  } = appointmentData;
  const isEveryDayInAWeekOfTheMonth =
    recurrencePeriod === REOCCURRENCE_PERIOD_TYPE_MONTHLY &&
    Boolean(recurrenceWeek);
  const staffMembersNames = staff_members
    .map((staff) => staff.fullName)
    .join(", ");
  const isTeamMeeting = isClinicEvent && staff_members.length > 1;

  const rruleText = getRRuleText(
    appointmentData.rrule || appointmentData.schedule?.rrule
  );

  return (
    <Grid container alignItems="flex-start" className={classes.titleContainer}>
      <Grid item xs={2} pt={0.75} className={classNames(classes.textCenter)}>
        <Stack>
          {Boolean(recurrencePeriod || rruleText) ? (
            <EventRepeatIcon />
          ) : (
            <EventIcon />
          )}
          {isTeamMeeting ? (
            <PeopleOutlinedIcon />
          ) : (
            isClinicEvent && <LocalActivityIcon />
          )}
        </Stack>
      </Grid>
      <Grid item xs={10}>
        <Stack>
          <Box
            display="flex"
            alignItems="center"
            className={classNames(classes.title, classes.dateAndTitle)}
            data-testid="personal-event-preview-title"
          >
            {title}
            {appointment_type?.name && (
              <Typography fontSize={12}>({appointment_type?.name})</Typography>
            )}
            {isGlobalPracticeEvent && <PublicIcon sx={{ ml: 1 }} />}
          </Box>
          <Typography
            className={classNames(classes.text, classes.dateAndTitle)}
          >
            {format(new Date(startDate), "EEEE, LLLL do")} /{" "}
            {`${formatDate(startDate, {
              hour: "numeric",
              minute: "numeric",
            })}
              - ${formatDate(endDate, {
                hour: "numeric",
                minute: "numeric",
              })}`}
          </Typography>
          {rruleText ? (
            <Typography>{rruleText}</Typography>
          ) : (
            <>
              {recurrencePeriod && !isEveryDayInAWeekOfTheMonth && (
                <Typography>
                  {REOCCURRENCE_PERIODS.getFromCode(recurrencePeriod)} on{" "}
                  {recurrencePeriod === REOCCURRENCE_PERIOD_TYPE_WEEKLY
                    ? RECURRENCE_DAYS.getFromCode(recurrenceDay)
                    : `the ${format(new Date(startDate), "do")}`}
                  {Boolean(endRecurrenceDate) && endRecurrenceDate !== null && (
                    <>
                      {" "}
                      until{" "}
                      {formatBackendDateOnly(
                        String(endRecurrenceDate),
                        "EEE MMM dd"
                      )}
                    </>
                  )}
                </Typography>
              )}
              {recurrencePeriod && isEveryDayInAWeekOfTheMonth && (
                <Typography>
                  {`${RECURRENCE_DAYS.getFromCode(
                    recurrenceDay
                  )}, ${RECURRENCE_WEEKS.getFromCode(recurrenceWeek)}`}
                  {Boolean(endRecurrenceDate) && endRecurrenceDate !== null && (
                    <>
                      {" "}
                      until{" "}
                      {formatBackendDateOnly(
                        String(endRecurrenceDate),
                        "EEE MMM dd"
                      )}
                    </>
                  )}
                </Typography>
              )}
            </>
          )}
        </Stack>
      </Grid>
      {multiClinic && !isGlobalPracticeEvent && (
        <Grid item xs={12} marginTop={1}>
          <PreviewContentRow
            Icon={LocationOnOutlinedIcon}
            content={clinic.name}
          />
        </Grid>
      )}

      {isTeamMeeting && staffMembersNames && (
        <PreviewContentRow
          Icon={PeopleOutlinedIcon}
          testId="event-preview-staff"
          content={staffMembersNames}
        />
      )}

      {notes && (
        <PreviewContentRow
          Icon={ContentPasteOutlinedIcon}
          content={
            <Typography sx={{ whiteSpace: "pre-line" }}>{notes}</Typography>
          }
          sx={{ mb: 2.5 }}
        />
      )}
    </Grid>
  );
}
